import { useEffect, useMemo } from 'react';
import { useObserver } from 'mobx-react-lite';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useQuery } from '@apollo/client';

import { GetRewardsV2Document } from 'src/gql/graphql';
import { useGetPosMedCardInfoQuery } from 'types/graphql';
import { useInstoreEnrollment } from 'src/hooks/use-instore-enrollment';
import { InstoreEnrollmentStatus } from 'src/state/instore-enrollment';
import useUser from 'src/hooks/use-user';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { useDutchiePay } from 'src/payments/hooks/use-dutchie-pay/use-dutchie-pay';
import { detectLoyaltyIntegration, Integrations } from 'src/components/loyalty/use-loyalty';
import { getLogger } from 'src/utils/datadog-logger';

type UseInstoreEnrollmentReturn = {
  currentStatus: InstoreEnrollmentStatus;
};

export function useInstoreEnrollmentPage(): UseInstoreEnrollmentReturn {
  const logger = useMemo(() => getLogger('instore-enrollment'), []);
  const instoreEnrollment = useInstoreEnrollment();
  const currentStatus = useObserver(() => instoreEnrollment.currentStatus);
  const user = useUser();
  const userIsLoggedIn = useObserver(() => user.isLoggedIn) ?? false;
  const userIsLoading = useObserver(() => user.loading) ?? true;

  // Reset enrollment state when user logs out
  useEffect(() => {
    if (!userIsLoggedIn && !userIsLoading) {
      // User has logged out, reset enrollment state
      instoreEnrollment.reset();
    }
  }, [userIsLoggedIn, userIsLoading, instoreEnrollment]);
  const { dispensary } = useDispensary();

  const {
    data: posMedCardInfoData,
    loading: posMedCardInfoLoading,
    error: posMedCardInfoError,
  } = useGetPosMedCardInfoQuery({
    skip: !dispensary?.id || !userIsLoggedIn,
    variables: {
      dispensaryId: dispensary?.id,
    },
  });

  // Memoize med card status calculation to avoid unnecessary recalculations
  const validMedCard = posMedCardInfoData?.getPosMedCardInfo?.valid ?? false;
  const hasValidMedCard = useMemo(() => (posMedCardInfoError ? false : validMedCard), [
    posMedCardInfoError,
    validMedCard,
  ]);

  // Update store when med card status changes
  useEffect(() => {
    instoreEnrollment.setHasValidMedCard(hasValidMedCard);
  }, [hasValidMedCard, instoreEnrollment]);

  const { data: rewardsV2Data, loading: rewardsV2DataLoading, error: rewardsV2Error } = useQuery(GetRewardsV2Document, {
    skip: !user.phone || !dispensary?.id,
    variables: {
      phoneNumber: user.phone,
      dispensaryId: dispensary?.id,
    },
  });

  // Log GraphQL query errors to Datadog
  useEffect(() => {
    if (posMedCardInfoError) {
      logger.error('Failed to fetch POS med card info', {
        error: posMedCardInfoError.message,
        dispensaryId: dispensary?.id,
      });
    }
  }, [posMedCardInfoError, dispensary?.id, logger]);

  useEffect(() => {
    if (rewardsV2Error) {
      logger.error('Failed to fetch rewards data', {
        error: rewardsV2Error.message,
        dispensaryId: dispensary?.id,
      });
    }
  }, [rewardsV2Error, dispensary?.id, logger]);

  const { isEnrolledDutchiePay } = useDutchiePay();
  const flags = useFlags();
  const isMedEnabled = flags['fintech.retail-payments.good-canna-integration-enabled.rollout'] ?? false;
  const completedMedIntake = useObserver(() => instoreEnrollment.completedMedIntake) ?? false;
  const completedLoyaltyStep = useObserver(() => instoreEnrollment.completedLoyaltyStep) ?? false;
  const rewardsProgram = detectLoyaltyIntegration(dispensary);
  const dispoHasRewardsProgram = rewardsProgram !== Integrations.none;
  const customerHasRewardsAccount = rewardsV2Data?.getRewardsV2?.userHasWallet;
  const shouldLoadRewardsData = Boolean(user.phone && dispensary?.id);

  // Step eligibility
  const medIntakeStepCompleted = !isMedEnabled || completedMedIntake || hasValidMedCard;
  const needsLoyaltyEnrollment = dispoHasRewardsProgram && !customerHasRewardsAccount && !completedLoyaltyStep;
  const needsBankConnection =
    (!dispoHasRewardsProgram || customerHasRewardsAccount || completedLoyaltyStep) && !isEnrolledDutchiePay;

  /**
   * ENROLLMENT STATUS PRIORITY (in order):
   * 1. If user is not logged in: status = ecommLogin
   * 2. If isMedEnabled && !completedMedIntake && !hasValidMedCard: status = medIntake
   * 3. If medIntakeStepCompleted && dispoHasRewardsProgram && !customerHasRewardsAccount && !completedLoyaltyStep: status = loyaltyEnrollment
   * 4. If medIntakeStepCompleted && (!dispoHasRewardsProgram || customerHasRewardsAccount || completedLoyaltyStep) && !isEnrolledDutchiePay: status = connectBank
   * 5. Otherwise: status = finishedEnrollment
   */

  useEffect(() => {
    const shouldLoadMedCardData = Boolean(dispensary?.id && userIsLoggedIn);
    const isStillLoading = (): boolean =>
      userIsLoading ||
      (shouldLoadRewardsData && rewardsV2DataLoading) ||
      (shouldLoadMedCardData && posMedCardInfoLoading);

    const determineEnrollmentStatus = (): InstoreEnrollmentStatus | null => {
      // Priority 1: User not logged in
      if (!userIsLoggedIn) {
        return InstoreEnrollmentStatus.ecommLogin;
      }

      // Validation: Check if user has required fields for enrollment
      const hasRequiredFields = user.phone && user.firstName && dispensary?.id;
      if (!hasRequiredFields) {
        return null; // Don't set any status - wait for user to complete profile
      }

      // Priority 2: Med intake required and not completed
      if (isMedEnabled && !completedMedIntake && !hasValidMedCard) {
        return InstoreEnrollmentStatus.medIntake;
      }

      // Priority 3: Loyalty enrollment needed
      if (medIntakeStepCompleted && needsLoyaltyEnrollment) {
        return InstoreEnrollmentStatus.loyaltyEnrollment;
      }

      // Priority 4: Connect bank account needed
      if (medIntakeStepCompleted && needsBankConnection) {
        return InstoreEnrollmentStatus.connectBankAccount;
      }

      // Priority 5: Fully enrolled
      return InstoreEnrollmentStatus.finishedEnrollment;
    };

    // Don't proceed if still loading critical data
    if (isStillLoading()) {
      return;
    }

    // Determine the appropriate status based on current conditions
    const newStatus = determineEnrollmentStatus();

    // Only set status if we have a valid status (not null)
    if (newStatus !== null) {
      instoreEnrollment.setCurrentStatus(newStatus);

      if (newStatus === InstoreEnrollmentStatus.finishedEnrollment) {
        instoreEnrollment.setIsFullyEnrolled();
      }
    }
  }, [
    completedLoyaltyStep,
    completedMedIntake,
    customerHasRewardsAccount,
    dispensary?.id,
    dispoHasRewardsProgram,
    hasValidMedCard,
    instoreEnrollment,
    isMedEnabled,
    isEnrolledDutchiePay,
    medIntakeStepCompleted,
    needsBankConnection,
    needsLoyaltyEnrollment,
    rewardsV2Data,
    rewardsV2DataLoading,
    shouldLoadRewardsData,
    user.firstName,
    user.phone,
    userIsLoading,
    userIsLoggedIn,
    posMedCardInfoLoading,
  ]);

  return { currentStatus };
}
